import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import { Components, Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { Message } from '../../../../../../contexts/chatSocket/reducer/chatSocketReducer.types';
import { useChatMessagesContext } from '../../../../contexts/chatMessages.context';
import { useChatMessagesContentStyles } from './chatMessagesContent.styles';
import ChatMessageForm from './components/ChatMessageForm/ChatMessageForm';
import ChatMessageItem from './components/ChatMessageItem/ChatMessageItem';
import useChatMessagesContent from './hooks/useChatMessagesContent';

const ChatMessagesContent = () => {
  const chatMessages = useChatMessagesContext();

  const { classes } = useChatMessagesContentStyles();

  const virtuosoRef = useRef<VirtuosoHandle | null>(null);
  const scrollerRef = useRef<HTMLElement | Window | null>(null);

  const {
    handleClickRetry,
    shouldShowUserAvatar,
    shouldShowMessageInfo,
    isMessageFromCurrentUser,
    chatMessageFormIsDisabled,
  } = useChatMessagesContent();

  const components: Components<Message> = {
    Header: () => (
      <Typography
        align="center"
        variant="subtitle2"
        color="textSecondary"
        className={classes.header}
      >
        {chatMessages.headerMessage}
      </Typography>
    ),
    Footer: () =>
      chatMessages.footerMessage ? (
        <Typography align="center" variant="subtitle2" color="textSecondary">
          {chatMessages.footerMessage}
        </Typography>
      ) : null,
  };

  useEffect(() => {
    if (!scrollerRef.current || !virtuosoRef.current || scrollerRef.current instanceof Window) {
      return;
    }

    const isNearBottom =
      scrollerRef.current?.offsetHeight + scrollerRef.current?.scrollTop >=
      scrollerRef.current?.scrollHeight - 100;

    if (isNearBottom) {
      setTimeout(() => {
        virtuosoRef.current?.scrollToIndex({ index: 'LAST', behavior: 'smooth' });
      }, 100);
    }
  }, [chatMessages.messages.length]);

  return (
    <Grid container direction="column">
      <Grid size="grow">
        <Box className={classes.list}>
          {chatMessages.isLoading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '280px',
              }}
            >
              <CircularProgress size={16} />
            </Box>
          )}

          {!chatMessages.isFirstLoading && (
            <Virtuoso
              ref={(ref) => (virtuosoRef.current = ref)}
              scrollerRef={(ref) => (scrollerRef.current = ref)}
              components={components}
              data={chatMessages.messages}
              firstItemIndex={chatMessages.firstItemIndex}
              startReached={chatMessages.fetchMoreMessages}
              initialTopMostItemIndex={chatMessages.messages.length}
              increaseViewportBy={{ bottom: 100, top: 100 }}
              className={classes.scrollableContent}
              itemContent={(_, message) => (
                <ChatMessageItem
                  message={message}
                  onClickRetry={handleClickRetry}
                  showUserAvatar={shouldShowUserAvatar(message)}
                  showMessageInfo={shouldShowMessageInfo(message)}
                  reserveSpaceOfUserAvatar={!isMessageFromCurrentUser(message)}
                />
              )}
            />
          )}
        </Box>
      </Grid>
      <Grid style={{ margin: '12px' }}>
        <ChatMessageForm disabled={chatMessageFormIsDisabled} />
      </Grid>
    </Grid>
  );
};

export default ChatMessagesContent;
